import * as React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from 'react-hot-toast';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { SubscriptionProvider } from './contexts/SubscriptionContext';
import { OnboardingProvider } from './contexts/OnboardingContext';
import Layout from './components/layout/Layout';

// Pages
import LoginPage from './pages/LoginPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import DashboardPage from './pages/DashboardPage';
import DispatcherDashboardPage from './pages/DispatcherDashboardPage';
import WorkOrdersPage from './pages/WorkOrdersPage';
import CreateWorkOrderPage from './pages/CreateWorkOrderPage';
import EditWorkOrderPage from './pages/EditWorkOrderPage';
import WorkOrderTemplates2Page from './pages/WorkOrderTemplates2Page';
import DefaultFirstPageEditorPage from './pages/DefaultFirstPageEditorPage';
import WorkOrderContactPage from './pages/WorkOrderContactPage';
import InvoicesPage from './pages/InvoicesPage';
import PaymentsPage from './pages/PaymentsPage';
import CreditMemosPage from './pages/CreditMemosPage';
import CreateCreditMemoPage from './pages/CreateCreditMemoPage';
import CreditMemoDetailsPage from './pages/CreditMemoDetailsPage';
import ClientsPage from './pages/ClientsPage';
import ClientDetailPage from './pages/ClientDetailPage';
import ClientEditPage from './pages/ClientEditPage';
import CategoriesPage from './pages/CategoriesPage';
import EquipmentPage from './pages/EquipmentPage';
import EquipmentDetailPage from './pages/EquipmentDetailPage';
import CreateEquipmentPage from './pages/CreateEquipmentPage';
import EditEquipmentPage from './pages/EditEquipmentPage';
import UnitsPage from './pages/UnitsPage';
import UnitDetailPage from './pages/UnitDetailPage';
import CreateUnitPage from './pages/CreateUnitPage';
import EditUnitPage from './pages/EditUnitPage';
import MaintenanceSchedulePage from './pages/MaintenanceSchedulePage';
import CreateMaintenanceSchedulePage from './pages/CreateMaintenanceSchedulePage';
import EditMaintenanceSchedulePage from './pages/EditMaintenanceSchedulePage';
import VendorsPage from './pages/VendorsPage';
import VendorDetailPage from './pages/VendorDetailPage';
import VendorEditPage from './pages/VendorEditPage';
import UsersPage from './pages/UsersPage';
import AdminPage from './pages/AdminPage';
import VendorComplianceSettingsPage from './pages/VendorComplianceSettingsPage';
import WorkflowNotificationSettingsPage from './pages/WorkflowNotificationSettingsPage';
import EmailIntegrationPage from './pages/EmailIntegrationPage';
import InboundEmailLogPage from './pages/InboundEmailLogPage';
import AdminDashboardPage from './pages/AdminDashboardPage';
import AdminNotificationsPage from './pages/AdminNotificationsPage';
import InvoiceEditorV2Page from './pages/InvoiceEditorV2Page';
import UserNotificationsPage from './pages/UserNotificationsPage';
import AdminNotificationCreatePage from './pages/AdminNotificationCreatePage';
import AuditLogsPage from './pages/AuditLogsPage';
import RolesPage from './pages/RolesPage';
import PrioritiesAdminPage from './pages/PrioritiesAdminPage';
import StatusAdminPage from './pages/StatusAdminPage';
import EntryTypesAdminPage from './pages/EntryTypesAdminPage';
import SuperAdminPage from './pages/SuperAdminPage';
import SubscriptionMetricsPage from './pages/SubscriptionMetricsPage';
import SystemAdminAnalyticsPage from './pages/SystemAdminAnalyticsPage';
import DataRetentionPage from './pages/DataRetentionPage';
import CreateCompanyPage from './pages/CreateCompanyPage';
import UserProfilePage from './pages/UserProfilePage';
import SystemAdminLoginPage from './pages/SystemAdminLoginPage';
import ReportsPage from './pages/ReportsPage';
import HelpPage from './pages/HelpPage';
import BrowserCheckPage from './pages/BrowserCheckPage';
import SubscriptionPage from './pages/settings/Subscription';
import StripeBillingPage from './pages/settings/StripeBillingPage';
import PaymentRequiredModal from './components/billing/PaymentRequiredModal';
import ScrollToTop from './components/ScrollToTop';

// Public Pages
import PublicLayout from './components/layout/PublicLayout';
import LandingPage from './pages/public/LandingPage';
import PricingPage from './pages/public/PricingPage';
import SignupPage from './pages/public/SignupPage';
import VerifyEmailPage from './pages/public/VerifyEmailPage';
import EstimateApprovalPage from './pages/public/EstimateApprovalPage';
import VendorCoiUploadPage from './pages/public/VendorCoiUploadPage';
import TermsPage from './pages/public/TermsPage';
import PrivacyPage from './pages/public/PrivacyPage';
import DesignDirectionsPage from './pages/public/DesignDirectionsPage';

// Industry Pages
import PropertyManagementIndustryPage from './pages/industries/PropertyManagementIndustryPage';
import FacilityMaintenanceIndustryPage from './pages/industries/FacilityMaintenanceIndustryPage';
import HealthcareIndustryPage from './pages/industries/HealthcareIndustryPage';
import EducationIndustryPage from './pages/industries/EducationIndustryPage';
import RetailIndustryPage from './pages/industries/RetailIndustryPage';
import ManufacturingIndustryPage from './pages/industries/ManufacturingIndustryPage';
import GovernmentIndustryPage from './pages/industries/GovernmentIndustryPage';
import HospitalityIndustryPage from './pages/industries/HospitalityIndustryPage';
import ReligiousIndustryPage from './pages/industries/ReligiousIndustryPage';
import CustomIndustryPage from './pages/industries/CustomIndustryPage';

// Create a client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

// Protected Route Component
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, isLoading, token } = useAuth();
  const isSystemAdmin = typeof window !== 'undefined' && localStorage.getItem('isSystemAdmin') === 'true';
  const tokenLocal = token || (typeof window !== 'undefined' ? localStorage.getItem('token') : null);

  // Debug logging
  console.log('ProtectedRoute Debug:', {
    isAuthenticated,
    isLoading,
    hasToken: !!token,
    hasTokenLocal: !!tokenLocal,
    isSystemAdmin,
    localStorageToken: typeof window !== 'undefined' ? localStorage.getItem('token') : 'N/A',
    localStorageIsSystemAdmin: typeof window !== 'undefined' ? localStorage.getItem('isSystemAdmin') : 'N/A'
  });

  // Wait for AuthContext to finish loading
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-accent-color"></div>
      </div>
    );
  }

  // For system admins, check if we have a token in localStorage even if AuthContext isn't ready
  if (isSystemAdmin && tokenLocal && !isAuthenticated) {
    console.log('ProtectedRoute: System admin with token, allowing access');
    return <Layout>{children}</Layout>;
  }

  if (!(isAuthenticated || (tokenLocal && isSystemAdmin))) {
    console.log('ProtectedRoute: Redirecting to login');
    // Redirect to appropriate login based on context
    return <Navigate to={isSystemAdmin ? "/systemadmin/login" : "/login"} replace />;
  }

  console.log('ProtectedRoute: Allowing access');
  return <Layout>{children}</Layout>;
};

const DashboardRoute: React.FC = () => {
  const { user } = useAuth();

  if (user?.roleName === 'Dispatcher') {
    return <Navigate to="/dispatcher/dashboard" replace />;
  }

  return <DashboardPage />;
};

// Admin Route Component
const AdminRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, isLoading, user, token } = useAuth();
  const isSystemAdmin = typeof window !== 'undefined' && localStorage.getItem('isSystemAdmin') === 'true';
  const tokenLocal = token || (typeof window !== 'undefined' ? localStorage.getItem('token') : null);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-accent-color"></div>
      </div>
    );
  }

  if (!(isAuthenticated || (tokenLocal && isSystemAdmin))) {
    // Redirect to appropriate login based on context
    return <Navigate to={isSystemAdmin ? "/systemadmin/login" : "/login"} replace />;
  }

  if (!isSystemAdmin && user?.roleName !== 'Admin' && user?.roleName !== 'SuperAdmin') {
    return <Navigate to="/dashboard" replace />;
  }

  return <Layout>{children}</Layout>;
};

// System Admin Route Component
const SystemAdminRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isLoading } = useAuth();
  const isSystemAdmin = typeof window !== 'undefined' && localStorage.getItem('isSystemAdmin') === 'true';

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-accent-color"></div>
      </div>
    );
  }

  if (!isSystemAdmin) {
    return <Navigate to="/systemadmin/login" replace />;
  }

  return <Layout>{children}</Layout>;
};

const App: React.FC = () => {
  const [paymentModalOpen, setPaymentModalOpen] = React.useState(false);
  const [paymentModalData, setPaymentModalData] = React.useState<{
    message?: string;
    feature?: string;
    requiredPlan?: string;
  }>({});

  React.useEffect(() => {
    const handleUpgradeEvent = (event: CustomEvent) => {
      setPaymentModalData({
        message: event.detail?.message,
        feature: event.detail?.feature,
        requiredPlan: event.detail?.requiredPlan,
      });
      setPaymentModalOpen(true);
    };

    window.addEventListener('subscription:upgrade-required', handleUpgradeEvent as EventListener);
    return () => {
      window.removeEventListener('subscription:upgrade-required', handleUpgradeEvent as EventListener);
    };
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <SubscriptionProvider>
        <ThemeProvider>
          <OnboardingProvider>
          <Router>
          <ScrollToTop />
          <Toaster 
            position="top-right"
            toastOptions={{
              duration: 4000,
              style: {
                background: '#363636',
                color: '#fff',
              },
            }}
          />
          <Routes>
            {/* Public Marketing Routes */}
            <Route element={<PublicLayout />}>
              <Route path="/" element={<LandingPage />} />
              <Route path="/design-directions" element={<DesignDirectionsPage />} />
              <Route path="/pricing" element={<PricingPage />} />
              <Route path="/signup" element={<SignupPage />} />
              <Route path="/terms" element={<TermsPage />} />
              <Route path="/privacy" element={<PrivacyPage />} />
              
              {/* Industry Pages */}
              <Route path="/industries/facilities" element={<FacilityMaintenanceIndustryPage />} />
              <Route path="/industries/healthcare" element={<HealthcareIndustryPage />} />
              <Route path="/industries/education" element={<EducationIndustryPage />} />
              <Route path="/industries/retail" element={<RetailIndustryPage />} />
              <Route path="/industries/manufacturing" element={<ManufacturingIndustryPage />} />
              <Route path="/industries/government" element={<GovernmentIndustryPage />} />
              <Route path="/industries/hospitality" element={<HospitalityIndustryPage />} />
              <Route path="/industries/religious" element={<ReligiousIndustryPage />} />
              <Route path="/industries/property-management" element={<PropertyManagementIndustryPage />} />
              <Route path="/industries/custom" element={<CustomIndustryPage />} />

              {/* Legacy redirects for old industry pages */}
              <Route path="/industries/hvac" element={<Navigate to="/industries/facilities" replace />} />
              <Route path="/industries/plumbing" element={<Navigate to="/industries/facilities" replace />} />
              <Route path="/industries/electrical" element={<Navigate to="/industries/facilities" replace />} />
              <Route path="/industries/general-contractors" element={<Navigate to="/industries/facilities" replace />} />
              <Route path="/industries/facility-maintenance" element={<Navigate to="/industries/facilities" replace />} />
            </Route>

            {/* Public Auth routes (no layout) */}
            <Route path="/login" element={<LoginPage />} />
            <Route path="/verify-email" element={<VerifyEmailPage />} />
            <Route path="/systemadmin/login" element={<SystemAdminLoginPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route path="/approve-estimate" element={<EstimateApprovalPage />} />
            <Route path="/upload-coi" element={<VendorCoiUploadPage />} />

            {/* Protected routes */}
            <Route path="/app" element={<Navigate to="/dashboard" replace />} />
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute>
                  <DashboardRoute />
                </ProtectedRoute>
              }
            />
            <Route
              path="/dispatcher/dashboard"
              element={
                <ProtectedRoute>
                  <DispatcherDashboardPage />
                </ProtectedRoute>
              }
            />
            {/* Promote V2 to primary route */}
            <Route
              path="/workorders"
              element={
                <ProtectedRoute>
                  <WorkOrdersPage />
                </ProtectedRoute>
              }
            />
            {/* Redirect old V2 route to primary */}
            <Route path="/work-orders-v2" element={<Navigate to="/workorders" replace />} />
            <Route
              path="/workorders/create"
              element={
                <ProtectedRoute>
                  <CreateWorkOrderPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/workorders/:id/edit"
              element={
                <ProtectedRoute>
                  <EditWorkOrderPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/workorders/templates"
              element={
                <ProtectedRoute>
                  <WorkOrderTemplates2Page />
                </ProtectedRoute>
              }
            />
            <Route
              path="/workorders/templates/first-page"
              element={
                <ProtectedRoute>
                  <DefaultFirstPageEditorPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/workorders/:id/contact"
              element={
                <ProtectedRoute>
                  <WorkOrderContactPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/invoices"
              element={
                <ProtectedRoute>
                  <InvoicesPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/invoices/:id/edit"
              element={
                <ProtectedRoute>
                  <InvoiceEditorV2Page />
                </ProtectedRoute>
              }
            />
        <Route
          path="/reports"
          element={
            <ProtectedRoute>
              <ReportsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/help"
          element={
            <ProtectedRoute>
              <HelpPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/help/browser-check"
          element={
            <ProtectedRoute>
              <BrowserCheckPage />
            </ProtectedRoute>
          }
        />
              <Route
                path="/invoices/create"
                element={
                  <ProtectedRoute>
                    <InvoiceEditorV2Page />
                  </ProtectedRoute>
                }
              />
            <Route
              path="/payments"
              element={
                <ProtectedRoute>
                  <PaymentsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/settings/subscription"
              element={
                <ProtectedRoute>
                  <SubscriptionPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/settings/stripe-billing"
              element={
                <ProtectedRoute>
                  <StripeBillingPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/credit-memos"
              element={
                <ProtectedRoute>
                  <CreditMemosPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/credit-memos/create"
              element={
                <ProtectedRoute>
                  <CreateCreditMemoPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/credit-memos/:id"
              element={
                <ProtectedRoute>
                  <CreditMemoDetailsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/clients"
              element={
                <ProtectedRoute>
                  <ClientsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/clients/:id"
              element={
                <ProtectedRoute>
                  <ClientDetailPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/clients/:id/edit"
              element={
                <ProtectedRoute>
                  <ClientEditPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/categories"
              element={
                <ProtectedRoute>
                  <CategoriesPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/equipment"
              element={
                <ProtectedRoute>
                  <EquipmentPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/equipment/create"
              element={
                <ProtectedRoute>
                  <CreateEquipmentPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/equipment/:id"
              element={
                <ProtectedRoute>
                  <EquipmentDetailPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/equipment/:id/edit"
              element={
                <ProtectedRoute>
                  <EditEquipmentPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/units"
              element={
                <ProtectedRoute>
                  <UnitsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/units/create"
              element={
                <ProtectedRoute>
                  <CreateUnitPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/units/:id"
              element={
                <ProtectedRoute>
                  <UnitDetailPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/units/:id/edit"
              element={
                <ProtectedRoute>
                  <EditUnitPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/maintenance-schedules"
              element={
                <ProtectedRoute>
                  <MaintenanceSchedulePage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/maintenance-schedules/create"
              element={
                <ProtectedRoute>
                  <CreateMaintenanceSchedulePage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/maintenance-schedules/:id/edit"
              element={
                <ProtectedRoute>
                  <EditMaintenanceSchedulePage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/vendors"
              element={
                <ProtectedRoute>
                  <VendorsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/vendors/:id"
              element={
                <ProtectedRoute>
                  <VendorDetailPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/vendors/:id/edit"
              element={
                <ProtectedRoute>
                  <VendorEditPage />
                </ProtectedRoute>
              }
            />
            
            {/* Admin-only routes */}
            <Route
              path="/users"
              element={
                <AdminRoute>
                  <UsersPage />
                </AdminRoute>
              }
            />
            <Route
              path="/admin"
              element={
                <AdminRoute>
                  <AdminPage />
                </AdminRoute>
              }
            />
            <Route
              path="/admin/vendor-compliance"
              element={
                <AdminRoute>
                  <VendorComplianceSettingsPage />
                </AdminRoute>
              }
            />
            <Route
              path="/admin/workflow-notifications"
              element={
                <AdminRoute>
                  <WorkflowNotificationSettingsPage />
                </AdminRoute>
              }
            />
            <Route
              path="/admin/email-integration"
              element={
                <AdminRoute>
                  <EmailIntegrationPage />
                </AdminRoute>
              }
            />
            <Route
              path="/admin/email-integration/log"
              element={
                <AdminRoute>
                  <InboundEmailLogPage />
                </AdminRoute>
              }
            />
            <Route
              path="/admin/dashboard"
              element={
                <AdminRoute>
                  <AdminDashboardPage />
                </AdminRoute>
              }
            />
            <Route
              path="/audit-logs"
              element={
                <AdminRoute>
                  <AuditLogsPage />
                </AdminRoute>
              }
            />
            <Route
              path="/roles"
              element={
                <AdminRoute>
                  <RolesPage />
                </AdminRoute>
              }
            />
            <Route
              path="/admin/priorities"
              element={
                <AdminRoute>
                  <PrioritiesAdminPage />
                </AdminRoute>
              }
            />
            <Route
              path="/admin/statuses"
              element={
                <AdminRoute>
                  <StatusAdminPage />
                </AdminRoute>
              }
            />
            <Route
              path="/admin/entry-types"
              element={
                <AdminRoute>
                  <EntryTypesAdminPage />
                </AdminRoute>
              }
            />
            {/* System Admin routes */}
            <Route
              path="/systemadmin"
              element={
                <SystemAdminRoute>
                  <SuperAdminPage />
                </SystemAdminRoute>
              }
            />
            <Route
              path="/systemadmin/data-retention"
              element={
                <SystemAdminRoute>
                  <DataRetentionPage />
                </SystemAdminRoute>
              }
            />
            <Route
              path="/systemadmin/companies/create"
              element={
                <SystemAdminRoute>
                  <CreateCompanyPage />
                </SystemAdminRoute>
              }
            />
            <Route
              path="/systemadmin/subscription-metrics"
              element={
                <SystemAdminRoute>
                  <SubscriptionMetricsPage />
                </SystemAdminRoute>
              }
            />
            <Route
              path="/systemadmin/analytics"
              element={
                <SystemAdminRoute>
                  <SystemAdminAnalyticsPage />
                </SystemAdminRoute>
              }
            />
            {/* Legacy redirects */}
            <Route path="/superadmin" element={<Navigate to="/systemadmin" replace />} />
            <Route path="/superadmin/companies/create" element={<Navigate to="/systemadmin/companies/create" replace />} />

            {/* Notifications */}
            <Route
              path="/notifications"
              element={
                <ProtectedRoute>
                  <UserNotificationsPage />
                </ProtectedRoute>
              }
            />
            
            {/* User Profile */}
            <Route
              path="/profile"
              element={
                <ProtectedRoute>
                  <UserProfilePage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/notifications"
              element={
                <AdminRoute>
                  <AdminNotificationsPage />
                </AdminRoute>
              }
            />
            <Route
              path="/admin/notifications/create"
              element={
                <AdminRoute>
                  <AdminNotificationCreatePage />
                </AdminRoute>
              }
            />
            
            {/* Catch all route */}
            <Route path="*" element={<Navigate to="/dashboard" replace />} />
          </Routes>
          
          {/* Payment Required Modal */}
          <PaymentRequiredModal
            isOpen={paymentModalOpen}
            onClose={() => setPaymentModalOpen(false)}
            message={paymentModalData.message}
            feature={paymentModalData.feature}
            requiredPlan={paymentModalData.requiredPlan}
          />
          
          </Router>
          </OnboardingProvider>
        </ThemeProvider>
        </SubscriptionProvider>
      </AuthProvider>
    </QueryClientProvider>
  );
};

export default App;
